import { useState, useCallback, useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import type { WidgetConfig, WidgetPosition } from './types/events';
import { ClientEventType } from './types/events';
import { ChatBubble, ChatWindow } from './components';
import { initI18n } from '@i18n';
import { DirectionContext, directionForLanguage, type Direction } from './i18n/useDirection';
import {
  useRouterSettings,
  useMessages,
  useSession,
  useWebSocket,
  useChatEventHandlers,
  LOCATION_SCOPE_TOOL_CALL_ID,
  PRECHAT_FORM_TOOL_CALL_ID,
  useIsMobile,
  useVisualViewport,
  useVisitorDirection,
  useWhatsAppHandoffBanner,
} from './hooks';

interface WidgetProps {
  config: WidgetConfig;
}

/**
 * Root Widget component that manages the bubble vs expanded state.
 * Renders either the floating chat bubble or the expanded chat window.
 */
export function Widget({ config }: WidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeLanguage, setActiveLanguage] = useState<string | undefined>(config.language);
  // Use channelId when available, fall back to routerId
  const widgetId = config.channelId || config.routerId;

  // Fetch router settings for customization (REST API)
  const { settings, error: settingsError } = useRouterSettings(config.apiHost, widgetId);

  const position: WidgetPosition = config.position ?? settings?.layout.position ?? 'bottom-right';
  // The visitor's own choice, kept only while the club allows one (see useVisitorDirection).
  const { visitorDirection, setVisitorDirection } = useVisitorDirection(widgetId);
  const allowDirectionChange = settings?.layout.allowDirectionChange === true;
  // Visitor beats embed override, which beats the channel's setting, which beats the language.
  // Resolved here and handed down through DirectionContext rather than derived per component from
  // the live i18n language: the agent can switch language mid-conversation, and that must not
  // unpin a choice the club — or the visitor — made.
  const configuredDirection: Direction =
    config.direction ?? settings?.layout.direction ?? directionForLanguage(activeLanguage);
  const direction: Direction =
    (allowDirectionChange ? visitorDirection : null) ?? configuredDirection;
  const paddingX = settings?.layout.paddingX ?? 16;
  const paddingY = settings?.layout.paddingY ?? 16;
  const isTop = position.startsWith('top');
  const isLeft = position.endsWith('left');

  const isMobile = useIsMobile();
  const isFullscreen = isMobile && isOpen;
  const viewport = useVisualViewport(isFullscreen);
  const rootRef = useRef<HTMLDivElement>(null);

  // Session management for persistence
  const session = useSession(widgetId);

  // Message state management
  const messageState = useMessages();

  // WebSocket connection
  const websocket = useWebSocket({
    wsHost: config.wsHost,
    routerId: config.routerId,
    channelId: config.channelId,
    sessionId: session.getRestorationSessionId(),
    autoConnect: isOpen, // Only connect when chat is open
  });

  const handoffBanner = useWhatsAppHandoffBanner(widgetId);

  // Wire up event handlers
  const { chatSettings, lastError, clearError } = useChatEventHandlers({
    websocket,
    messages: messageState,
    session,
    onChatSettings: (chatSettings) => {
      if (typeof chatSettings.language === 'string' && chatSettings.language.trim()) {
        setActiveLanguage(chatSettings.language.trim());
      }
      // Re-read on every chat_settings, not just the first: the server re-sends it after
      // a restore or a club pick, and either can bring the banner or take it away.
      handoffBanner.setAvailable(chatSettings.whatsapp_handoff ?? null);
    },
    onWhatsAppHandoff: handoffBanner.offer,
    onLanguageUpdate: (language) => {
      if (language.trim()) {
        setActiveLanguage(language.trim());
      }
    },
  });

  // Whether the server is still waiting for contact details. Read from the settings rather
  // than from a card in the transcript: the settings are the authority, and a visitor whose
  // card is missing for any reason would otherwise face an open message box, a refusal from
  // the server and no way forward.
  const prechatRequired =
    !!chatSettings?.prechat_form && chatSettings.prechat_form.satisfied === false;


  useEffect(() => {
    if (settings?.settings.language && settings.settings.language.trim()) {
      setActiveLanguage(settings.settings.language.trim());
    }
  }, [settings?.settings.language]);

  useEffect(() => {
    if (activeLanguage && activeLanguage.trim()) {
      initI18n(activeLanguage.trim());
    }
  }, [activeLanguage]);

  useEffect(() => {
    if (!isFullscreen || typeof document === 'undefined') return;
    const { documentElement, body } = document;
    const scrollY = typeof window !== 'undefined' ? window.scrollY : 0;
    const prev = {
      htmlOverflow: documentElement.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
    };
    documentElement.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';

    const root = rootRef.current;
    const onTouchMove = (e: TouchEvent) => {
      let el = e.target as HTMLElement | null;
      while (el && el !== root) {
        if (el.scrollHeight > el.clientHeight) {
          const overflowY = getComputedStyle(el).overflowY;
          if (overflowY === 'auto' || overflowY === 'scroll') return;
        }
        el = el.parentElement;
      }
      if (e.cancelable) e.preventDefault();
    };
    root?.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      documentElement.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.left = prev.bodyLeft;
      body.style.right = prev.bodyRight;
      body.style.width = prev.bodyWidth;
      root?.removeEventListener('touchmove', onTouchMove);
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        window.scrollTo(0, scrollY);
      }
    };
  }, [isFullscreen]);

  const toggleChat = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSendMessage = useCallback(
    (message: string) => {
      // Add message to local state immediately for optimistic UI
      messageState.addUserMessage(message);
      messageState.setAssistantTyping(true);
      // Send via WebSocket
      websocket.sendMessage(message);
    },
    [messageState, websocket]
  );

  const handleStarterClick = useCallback(
    (text: string) => {
      setIsOpen(true);
      // Delay sending so WebSocket connects first
      setTimeout(() => handleSendMessage(text), 300);
    },
    [handleSendMessage]
  );

  const handleSendToolResponse = useCallback(
    (toolCallId: string, toolName: string, result: Record<string, unknown>) => {
      // The open-time location question is the widget's own prompt, not a tool the assistant
      // called, so answering it scopes the conversation and expects no reply. Showing a typing
      // indicator here would promise one that never arrives.
      // The pre-chat form is the widget's own prompt too, and it gates the chat rather
      // than answering the assistant: no tool ran, so nothing is waiting on a result and
      // a typing indicator would promise a reply that never comes.
      if (toolCallId === PRECHAT_FORM_TOOL_CALL_ID) {
        const locationId = Number(result.location_id);
        if (Number.isFinite(locationId) && locationId > 0) {
          session.saveLocationId(locationId);
        }
        session.saveContactDetails({
          first_name: String(result.first_name ?? ''),
          mobile_phone: String(result.mobile_phone ?? ''),
          email: String(result.email ?? ''),
          ...(result.location_id ? { location_id: String(result.location_id) } : {}),
        });
        websocket.send({
          type: ClientEventType.SUBMIT_PRECHAT_FORM,
          details: result,
        });
        return;
      }

      if (toolCallId === LOCATION_SCOPE_TOOL_CALL_ID) {
        const locationId = Number(result.location_id);
        if (Number.isFinite(locationId) && locationId > 0) {
          session.saveLocationId(locationId);
          websocket.send({
            type: ClientEventType.SELECT_LOCATION,
            location_id: locationId,
          });
        }
        return;
      }

      messageState.setAssistantTyping(true);
      websocket.send({
        type: ClientEventType.TOOL_RESPONSE,
        tool_call_id: toolCallId,
        tool_name: toolName,
        result,
      });
    },
    [messageState, session, websocket]
  );

  const containerStyle: CSSProperties = isFullscreen
    ? {
        top: viewport ? `${viewport.offsetTop}px` : 0,
        left: 0,
        width: '100dvw',
        height: viewport ? `${viewport.height}px` : '100dvh',
        margin: 0,
      }
    : isTop
      ? (isLeft
          ? { top: 0, left: 0, marginTop: `${paddingY}px`, marginLeft: `${paddingX}px` }
          : { top: 0, right: 0, marginTop: `${paddingY}px`, marginRight: `${paddingX}px` })
      : (isLeft
          ? { bottom: 0, left: 0, marginBottom: `${paddingY}px`, marginLeft: `${paddingX}px` }
          : { bottom: 0, right: 0, marginBottom: `${paddingY}px`, marginRight: `${paddingX}px` });
  const alignItems = isLeft ? 'flex-start' : 'flex-end';
  const flexDirection = isTop ? 'column-reverse' : 'column';

  // Hide the widget until settings have loaded so it doesn't flash at the
  // hardcoded default position before re-rendering at the configured corner.
  // On settings error we fall through and render with defaults so the widget
  // is still usable.
  if (!settings && !settingsError) return null;

  return (
    <DirectionContext.Provider value={direction}>
      <div ref={rootRef} className="clubplanner-widget-root" style={containerStyle} dir={direction}>
        {isOpen ? (
          <ChatWindow
            onClose={handleClose}
            prechatRequired={prechatRequired}
            settings={settings}
            messages={messageState.messages}
            isStreaming={messageState.isStreaming}
            isAssistantTyping={messageState.isAssistantTyping}
            connectionStatus={websocket.status}
            onSendMessage={handleSendMessage}
            onSendToolResponse={handleSendToolResponse}
            error={lastError}
            onClearError={clearError}
            horizontalViewportPadding={paddingX}
            isFullscreen={isFullscreen}
            keyboardOpen={viewport?.keyboardOpen ?? false}
            whatsAppHandoff={handoffBanner.banner}
            onDismissWhatsAppHandoff={handoffBanner.dismiss}
            // Stable across reconnects/reloads. NOT the session id — the backend
            // rotates that when it can't restore a session, which would reset the
            // dismissal state and re-pop a closed sheet.
            persistKey={widgetId}
            // Only rendered when the club opted in; flipping stores the visitor's own choice, which
            // from then on outranks the club's setting for this browser.
            onToggleDirection={
              allowDirectionChange
                ? () => setVisitorDirection(direction === 'rtl' ? 'ltr' : 'rtl')
                : undefined
            }
          />
        ) : (
          <div style={{ display: 'flex', flexDirection, alignItems, gap: '8px' }}>
            {settings?.settings.conversationStarters && settings.settings.conversationStarters.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems }}>
                {settings.settings.conversationStarters.slice(0, 2).map((text) => (
                  <button
                    key={text}
                    onClick={() => handleStarterClick(text)}
                    style={{
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '9999px',
                      padding: '6px 14px',
                      fontSize: '13px',
                      color: '#334155',
                      cursor: 'pointer',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      whiteSpace: 'nowrap',
                      lineHeight: '1.4',
                      fontFamily: 'inherit',
                      transition: 'box-shadow 0.15s, border-color 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.12)';
                      e.currentTarget.style.borderColor = '#cbd5e1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                    }}
                    type="button"
                  >
                    {text}
                  </button>
                ))}
              </div>
            )}
            <ChatBubble onClick={toggleChat} settings={settings} />
          </div>
        )}
      </div>
    </DirectionContext.Provider>
  );
}

export default Widget;
